import { eventNames, useCoreEvents } from '@carvana/core-events';
import LinkButton from '@carvana/ds-link-button';
import { icon } from '@carvana/ds-styles';
import DispatcherEvent from '@carvana/merch-native/DispatcherEvent';
import { usePromise } from '@carvana/merch-react-hooks';
import { Heart } from 'lucide-react';
import { useContext, useState } from 'react';
import { ToastActions } from '@/common/FavoritesToastContent/types';
import useDisplayFavoritesToast from '@/common/FavoritesToastContent/useDisplayFavoritesToast';
import AppliedFiltersContext from '~/context/common/AppliedFiltersContext';
import SavedSearchesContext from '~/context/common/SavedSearchesContext';
import {
  useUserEmail,
  useUserId,
  useUserLocation
} from '~/context/common/UserContext';
import useIsSavableSearch from '~/hooks/srp/selectors/useIsSavableSearch';
import useLogInAndSaveSearch from '~/hooks/srp/useLogInAndSaveSearch';
import { useCurrentSavedSearchId } from '~/hooks/srp/useSavedSearchApplied';
import sendMessageToNative from '~/native/srp/dispatchers/sendMessageToNative';
import { deleteSavedSearchRCB } from '~/services/common/savedSearch';
import { BrowserCookies } from '~/utilities';
import { generateErrorLogger } from '~/utilities/loggers';

const SaveSearchButton = () => {
  const { appliedFilters } = useContext(AppliedFiltersContext);
  const { savedSearches } = useContext(SavedSearchesContext);
  const { emit } = useCoreEvents();
  const { zip } = useUserLocation();
  const mounted = usePromise();
  const displayFavoritesToast = useDisplayFavoritesToast();
  const userId = useUserId();
  const email = useUserEmail();
  const saveSearch = useLogInAndSaveSearch();
  const savedSearchId = useCurrentSavedSearchId(appliedFilters);
  const isSavableSearch = useIsSavableSearch(appliedFilters);

  const [isDeleting, setIsDeleting] = useState(false);

  const browserCookieId = BrowserCookies.get('BrowserCookieId') ?? '';
  const savedSearchIsApplied = savedSearchId !== null;
  const savedSearchIsDisabled = !isSavableSearch || isDeleting;
  const deleteSearch = () => {
    if (!savedSearchId) return;

    setIsDeleting(true);

    mounted(
      deleteSavedSearchRCB({
        browserCookieId,
        email,
        savedSearchId,
        userId,
        zip5: zip
      })
    )
      .then(res => {
        savedSearches.setData(res.data ?? []);
        sendMessageToNative(DispatcherEvent.SavedSearchDeleted, {});
        emit(eventNames.favorites.mfe.refresh, {
          searches: res.data,
          source: 'app'
        });
        displayFavoritesToast(ToastActions.remove);
      })
      .catch(generateErrorLogger('Delete saved search'))
      .finally(() => setIsDeleting(false));
  };

  const handleClick = () => {
    if (savedSearchIsApplied) {
      deleteSearch();
    } else {
      saveSearch();
    }
  };

  return (
    <LinkButton
      className="flex items-center gap-sm bg-transparent sm:gap-md"
      data-testid="Save-Search"
      variant="default"
      size="sm"
      underline={false}
      data-adobe-id={null}
      data-analytics-id="Save Search"
      onClick={handleClick}
      disabled={savedSearchIsDisabled}
      data-analytics-attributes={JSON.stringify({
        description: 'save search button',
        action: savedSearchIsApplied ? 'remove' : 'add'
      })}
    >
      {savedSearchIsApplied && !isDeleting ? (
        <Heart size={16} color={icon.critical} className="fill-icon-critical" />
      ) : (
        <Heart
          size={16}
          color={savedSearchIsDisabled ? icon.disabled : 'currentColor'}
        />
      )}
      <span>
        {savedSearchIsApplied ? (
          'Saved'
        ) : (
          <>
            Save<span className="hidden sm:inline"> Search</span>
          </>
        )}
      </span>
    </LinkButton>
  );
};

export default SaveSearchButton;
